import {
  Input as ChakraInput,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
} from '@chakra-ui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { IconDotGrid1x3Vertical } from 'central-icons/IconDotGrid1x3Vertical';
import { type KeyboardEvent, useState } from 'react';

import { LoadingBox } from '@revelio/core';
import { Button, Option, Text } from '@revelio/ui';

import { AlertDialog } from '../../../shared/components/alert-dialog';
import { hydrateStoreFromPayload } from '../api/hydrate-store-from-payload';
import {
  deleteDataBuilderDataset,
  listDataBuilderDatasets,
  updateDataBuilderDataset,
} from '../api/save-dataset-api';
import { DataBuilderFooter } from '../components/DataBuilderFooter';
import { DataBuilderGrid } from '../components/ui/DataBuilderGrid';
import { DataBuilderOption } from '../components/ui/DataBuilderOption';
import { DataBuilderStepLayout } from '../components/ui/DataBuilderStepLayout';
import { dataBuilderStore } from '../data-builder.store';
import { useDataBuilderNavigation } from '../useDataBuilderNavigation';

const QUERY_KEY = ['data-builder-saved-datasets'];

const formatSavedDatasetDate = (value: string) => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export const DataBuilderSavedDatasetsStep = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { goToDatasetStep } = useDataBuilderNavigation();
  const { data: datasets = [], isLoading } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: listDataBuilderDatasets,
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const selectedDataset =
    selectedId === null
      ? undefined
      : datasets.find((dataset) => dataset._id === selectedId);
  const editingDataset =
    editingId === null
      ? undefined
      : datasets.find((dataset) => dataset._id === editingId);

  const handleContinue = () => {
    if (selectedDataset === undefined) {
      return;
    }

    const hydratedState = hydrateStoreFromPayload(selectedDataset.payload);
    dataBuilderStore.setState({
      ...hydratedState,
      loadedSavedDatasetId: selectedDataset._id,
    });
    goToDatasetStep();
  };

  const handleEditStart = (id: string, currentName: string) => {
    setEditingId(id);
    setEditingName(currentName);
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditingName('');
  };

  const handleRename = async (id: string) => {
    const dataset = datasets.find((item) => item._id === id);
    const nextName = editingName.trim();
    if (dataset === undefined || nextName.length === 0) {
      return;
    }

    setSavingId(id);
    try {
      await updateDataBuilderDataset({
        id: dataset._id,
        name: nextName,
        payload: dataset.payload,
      });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      handleEditCancel();
      // biome-ignore lint/correctness/noUnusedVariables: migrated from develop
    } catch (error: unknown) {
      toast({
        status: 'error',
        title: 'Could not rename saved dataset',
        description: 'Please try again.',
      });
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    setConfirmDeleteId(null);
    setDeletingId(id);
    try {
      await deleteDataBuilderDataset(id);
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      if (selectedId === id) {
        setSelectedId(null);
      }
      // biome-ignore lint/correctness/noUnusedVariables: migrated from develop
    } catch (error: unknown) {
      toast({
        status: 'error',
        title: 'Could not delete saved dataset',
        description: 'Please try again.',
      });
    } finally {
      setDeletingId(null);
    }
  };

  const handleRenameKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (editingId === null) {
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      void handleRename(editingId);
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      handleEditCancel();
    }
  };

  const hasDatasets = datasets.length > 0;

  return (
    <DataBuilderStepLayout
      title="Select Saved Datasets"
      footer={
        <DataBuilderFooter
          actions={
            <Button
              size="sm"
              color="primary"
              disabled={selectedDataset === undefined}
              onClick={handleContinue}
            >
              Continue
            </Button>
          }
        />
      }
    >
      <LoadingBox loading={isLoading}>
        <DataBuilderGrid columns="2" style={{ paddingBottom: '12px' }}>
          {hasDatasets &&
            datasets.map((dataset) => {
              const isRenaming = savingId === dataset._id;
              const isDeleting = deletingId === dataset._id;
              const isSelected = selectedId === dataset._id;
              const formattedUpdatedAt = formatSavedDatasetDate(
                dataset.updatedAt
              );

              return (
                <Option
                  key={dataset._id}
                  data-testid={`saved-dataset-option-${dataset._id}`}
                  style={{ minHeight: '64px', alignItems: 'flex-start' }}
                  isSelected={isSelected}
                  onClick={() => setSelectedId(dataset._id)}
                  startSlot={
                    <DataBuilderOption.Radio isSelected={isSelected} />
                  }
                  startSlotAlignment="center"
                  contentAlignment="start"
                  endSlot={
                    <Menu placement="bottom-end" offset={[0, 2]}>
                      <MenuButton
                        as={IconButton}
                        icon={<IconDotGrid1x3Vertical size={14} />}
                        aria-label="Saved dataset actions"
                        size="xs"
                        variant="ghost"
                        colorScheme="gray"
                        borderRadius="4px"
                        onClick={(event) => event.stopPropagation()}
                        data-testid={`saved-dataset-actions-${dataset._id}`}
                        isLoading={isRenaming || isDeleting}
                      />
                      <MenuList onClick={(event) => event.stopPropagation()}>
                        <MenuItem
                          data-testid={`saved-dataset-rename-${dataset._id}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleEditStart(dataset._id, dataset.name);
                          }}
                          isDisabled={isRenaming || isDeleting}
                        >
                          Rename
                        </MenuItem>
                        <MenuItem
                          data-testid={`saved-dataset-delete-${dataset._id}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            setConfirmDeleteId(dataset._id);
                          }}
                          isDisabled={isDeleting}
                        >
                          Delete
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  }
                >
                  <div>
                    <DataBuilderOption.Label>
                      {dataset.name}
                    </DataBuilderOption.Label>
                    {formattedUpdatedAt && (
                      <DataBuilderOption.Description>
                        {`Last updated ${formattedUpdatedAt}`}
                      </DataBuilderOption.Description>
                    )}
                  </div>
                </Option>
              );
            })}
          {!hasDatasets && <Text fontSize="md">No saved datasets found.</Text>}
        </DataBuilderGrid>
      </LoadingBox>
      <Modal
        isOpen={editingDataset !== undefined}
        onClose={handleEditCancel}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color="text.primary">Rename Saved Dataset?</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <ChakraInput
              value={editingName}
              onChange={(event) => setEditingName(event.target.value)}
              onKeyDown={handleRenameKeyDown}
              placeholder="Enter a dataset name"
              autoFocus
            />
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              style={{ marginRight: '12px' }}
              onClick={handleEditCancel}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={() => {
                if (editingId !== null) {
                  void handleRename(editingId);
                }
              }}
              disabled={
                editingName.trim().length === 0 ||
                (editingId !== null && savingId === editingId)
              }
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {confirmDeleteId !== null ? (
        <AlertDialog
          isOpen={true}
          alertType="confirm"
          skipDefaultDisclosure={true}
          chakraAlertProps={{ isCentered: true }}
          onClose={() => setConfirmDeleteId(null)}
          headerText="Want to Delete This Saved Dataset?"
          bodyContent={
            <span>
              {
                datasets.find((dataset) => dataset._id === confirmDeleteId)
                  ?.name
              }{' '}
              will be permanently removed. Any previous runs will still be
              available.
            </span>
          }
          footerAcceptIsDestructive
          footerAcceptText="Yes, Delete Dataset"
          onAccept={() => {
            void handleDelete(confirmDeleteId);
          }}
        />
      ) : null}
    </DataBuilderStepLayout>
  );
};
